import React from "react";
import { AlertTriangle, ShieldCheck, LayoutGrid, Terminal, FileSpreadsheet, Sparkles } from "lucide-react";

/** Where a ruleset object came from: the customer's own GRC Access Control
 * export, or the UTSAVA-curated APO/CRM/SRM enrichment overlay (which must be
 * validated against the customer's SU24/PFCG data before being cited as audit
 * evidence). Rendered on Functions, SoD risks and critical activities. */
export function ProvenanceBadge({ provenance, enrichment, testId, className = "" }) {
    const enriched = provenance === "utsava_enriched";
    if (!enriched) {
        return (
            <span
                data-testid={testId}
                title="Sourced directly from the customer-supplied GRC Access Control export"
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wider border bg-white/5 text-slate-400 border-white/10 cursor-help ${className}`}
            >
                <FileSpreadsheet size={9} /> grc export
            </span>
        );
    }
    const confidence = enrichment?.confidence || "high";
    return (
        <span
            data-testid={testId}
            title={
                (enrichment?.basis ? `${enrichment.basis}\n\n` : "") +
                "UTSAVA-curated enrichment overlay — the GRC export carries no Action/Permission rows for this Activity. " +
                (confidence === "medium"
                    ? "Medium confidence: validate against your own SU24/PFCG data before citing as audit evidence."
                    : "Mapped to SAP standard-delivery transactions/authorization objects for this module.")
            }
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wider border cursor-help ${
                confidence === "medium"
                    ? "bg-amber-500/10 text-amber-300 border-amber-500/30"
                    : "bg-violet-500/10 text-violet-300 border-violet-500/30"
            } ${className}`}
        >
            <Sparkles size={9} /> enriched · {confidence}
        </span>
    );
}

export function SeverityBadge({ severity, className = "" }) {
    const s = severity || "info";
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider border sev-${s} ${className}`}
        >
            <span className="dot" style={{ background: "currentColor", boxShadow: "0 0 8px currentColor" }} />
            {s}
        </span>
    );
}

export function StatusBadge({ status, className = "" }) {
    const map = {
        open: { cls: "sev-info", label: "open" },
        in_review: { cls: "sev-info", label: "in review" },
        approved: { cls: "sev-safe", label: "approved" },
        rejected: { cls: "sev-critical", label: "rejected" },
        draft: { cls: "sev-info", label: "draft" },
        active: { cls: "sev-safe", label: "active" },
        archived: { cls: "sev-info", label: "archived" },
    };
    const { cls, label } = map[status] || { cls: "sev-info", label: status };
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider border ${cls} ${className}`}
        >
            {label}
        </span>
    );
}

export function ConfidenceMeter({ value, label = "Confidence" }) {
    const pct = Math.round((value || 0) * 100);
    return (
        <div className="flex items-center gap-2 text-[11px] font-mono text-slate-400">
            <span className="uppercase tracking-wider">{label}</span>
            <div className="w-20 h-1 bg-slate-800 rounded-full overflow-hidden">
                <div
                    className="h-full bg-gradient-to-r from-cyan-400 to-cyan-600"
                    style={{ width: `${pct}%` }}
                />
            </div>
            <span className="text-cyan-300">{pct}%</span>
        </div>
    );
}

/** Flags an SoD/critical-access match's evidence tier:
 * - "medium": a shared generic Authorization Object with no real field
 *   values captured yet — the classic object-name-level over-match risk
 *   (see PRD "Field/ACTVT-level SoD granularity").
 * - "field_verified": the role's REAL captured ACTVT values (Field Editor)
 *   were checked against the function's requirement and genuinely overlap —
 *   a confirmed, field-verified violation, not a guess. */
export function FieldConfidenceBadge({ confidence, note, className = "" }) {
    if (confidence === "field_verified") {
        return (
            <span
                title={note}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wider border bg-emerald-500/10 text-emerald-300 border-emerald-500/30 cursor-help ${className}`}
            >
                <ShieldCheck size={9} /> field-verified · confirmed
            </span>
        );
    }
    if (confidence !== "medium") return null;
    return (
        <span
            title={note}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wider border bg-amber-500/10 text-amber-300 border-amber-500/30 cursor-help ${className}`}
        >
            <AlertTriangle size={9} /> object-level match · review field
        </span>
    );
}

/** Flags whether an engineered role has real Fiori Business Catalog coverage
 * (`role.fiori_catalogs` non-empty — generated/migrated onto Fiori Launchpad
 * catalogs) vs being T-code-only (legacy PFCG, a candidate for Fiori
 * migration via the "Convert to Catalog-Based" tool). */
export function CatalogCoverageBadge({ role, testId, className = "" }) {
    const hasCatalogs = (role?.fiori_catalogs || []).length > 0;
    if (hasCatalogs) {
        return (
            <span
                data-testid={testId}
                title={`Covered by ${role.fiori_catalogs.length} Fiori Business Catalog(s)`}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wider border bg-cyan-500/10 text-cyan-300 border-cyan-500/30 cursor-help ${className}`}
            >
                <LayoutGrid size={9} /> fiori catalog · {role.fiori_catalogs.length}
            </span>
        );
    }
    return (
        <span
            data-testid={testId}
            title="No Fiori Business Catalog coverage yet — a candidate for the Fiori Migration tool"
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wider border bg-white/5 text-slate-400 border-white/10 cursor-help ${className}`}
        >
            <Terminal size={9} /> t-code only
        </span>
    );
}
